import React, { useState, useEffect } from 'react';
import { ArrowLeft, CreditCard, Truck, ShieldCheck, Lock, CheckCircle2, MapPin, Plus, Minus, Trash2, Printer, Upload, AlertCircle, Clock, FileText, Building, Store, LogIn, UserPlus, Mail, Phone, Sparkles, X } from 'lucide-react';
import { CartItem, Product, MemberOrder, User } from '../types';
import { fetchBankDetails, BankDetail, getDefaultBank } from '../services/bankDetails';

interface CheckoutProps {
  cart: CartItem[];
  onPlaceOrder: (order: MemberOrder) => void;
  onNavigate: (page: string) => void;
  onSelectProduct?: (product: Product) => void;
  onUpdateCartQty?: (id: number, delta: number) => void;
  onRemoveFromCart?: (id: number) => void;
  onClearCart?: () => void;
  directCheckoutItem?: { product: Product; quantity: number } | null;
  user?: User | null;
  onRefreshWishlist?: () => void;
}

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

// Placeholder image
const PLACEHOLDER_IMAGE = 'https://placehold.co/400x400/f5f5f5/999999?text=No+Image';

// ============================================================
// FIXED: repairBase64Image - Fixes malformed base64 strings
// ============================================================
const repairBase64Image = (imageUrl: string | undefined): string => {
  if (!imageUrl || typeof imageUrl !== 'string') return PLACEHOLDER_IMAGE;
  
  const trimmed = imageUrl.trim();
  if (!trimmed) return PLACEHOLDER_IMAGE;
  
  // Fix malformed base64 (missing semicolon or comma)
  if (trimmed.startsWith('data:image/')) {
    let fixed = trimmed;
    
    // Fix missing semicolon before base64 (data:image/jpeg:base64 -> data:image/jpeg;base64,)
    if (fixed.includes(':base64') && !fixed.includes(';base64')) {
      fixed = fixed.replace(':base64', ';base64,');
      console.log('🔧 Repaired malformed base64');
    }
    
    // Fix missing comma after base64
    if (fixed.includes(';base64') && !fixed.includes(';base64,')) {
      fixed = fixed.replace(';base64', ';base64,');
      console.log('🔧 Repaired missing comma');
    }
    
    return fixed;
  }
  
  // Keep blob URLs
  if (trimmed.startsWith('blob:')) {
    return trimmed;
  }
  
  // Keep absolute URLs
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  
  // Handle relative paths
  if (trimmed.startsWith('/')) {
    const SITE_BASE_URL = process.env.NODE_ENV === 'production' 
      ? 'https://sparklinggems.shop'
      : 'http://localhost/sparkling-gems-backend-php';
    return `${SITE_BASE_URL}${trimmed}`;
  }
  
  return PLACEHOLDER_IMAGE;
};

// ============================================================
// Image compression utilities
// ============================================================
const compressImage = (base64: string, maxWidth = 800, quality = 0.6): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      
      const compressed = canvas.toDataURL('image/jpeg', quality);
      resolve(compressed);
    };
    img.onerror = reject;
  });
};

// Helper function to format date for receipt
const formatReceiptDate = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };
  return date.toLocaleString('en-PH', options);
};

// Generate receipt HTML for printing
const generateReceiptHTML = (order: MemberOrder, user: User | null, profileData: any) => {
  const now = new Date();
  const orderDate = order.orderDate ? new Date(order.orderDate) : now;
  const isPickup = (order as any).deliveryType === 'pickup' || (order as any).delivery_type === 'pickup';
  const isPaid = order.status === 'Paid';
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Sparkling Gems - Official Receipt</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; background: #f5f5f5; display: flex; justify-content: center; align-items: center; min-height: 100vh; padding: 20px; }
        .receipt { max-width: 400px; width: 100%; background: white; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); overflow: hidden; }
        .receipt-content { padding: 24px 20px; }
        .header { text-align: center; border-bottom: 1px dashed #ccc; padding-bottom: 16px; margin-bottom: 16px; }
        .store-name { font-size: 20px; font-weight: bold; letter-spacing: 2px; color: #1a1a1a; }
        .store-tagline { font-size: 9px; color: #b4945c; letter-spacing: 1px; margin-top: 4px; }
        .store-address { font-size: 8px; color: #666; margin-top: 8px; line-height: 1.3; }
        .receipt-title { font-size: 11px; font-weight: bold; letter-spacing: 2px; margin-top: 12px; color: #333; }
        .info-section { border-bottom: 1px dashed #eee; padding-bottom: 12px; margin-bottom: 12px; }
        .info-row { display: flex; justify-content: space-between; font-size: 9px; margin-bottom: 4px; font-family: monospace; }
        .info-label { color: #666; }
        .info-value { color: #333; font-weight: 500; }
        .items-header { display: flex; justify-content: space-between; font-size: 9px; font-weight: bold; padding-bottom: 6px; margin-bottom: 8px; border-bottom: 1px solid #ddd; text-transform: uppercase; letter-spacing: 0.5px; }
        .item-row { display: flex; justify-content: space-between; font-size: 9px; margin-bottom: 6px; line-height: 1.3; }
        .item-name { flex: 2; color: #333; word-break: break-word; }
        .item-qty { width: 35px; text-align: center; color: #666; }
        .item-price { width: 70px; text-align: right; color: #333; }
        .totals-section { border-top: 1px dashed #ccc; padding-top: 12px; margin-top: 12px; }
        .total-row { display: flex; justify-content: space-between; font-size: 9px; margin-bottom: 4px; }
        .grand-total { border-top: 1px solid #333; margin-top: 8px; padding-top: 6px; font-weight: bold; font-size: 11px; }
        .payment-method { margin-top: 12px; padding-top: 8px; border-top: 1px dashed #eee; }
        .footer { text-align: center; border-top: 1px dashed #ccc; padding-top: 16px; margin-top: 16px; font-size: 8px; color: #888; }
        .qr-note { margin-top: 12px; font-size: 7px; color: #aaa; text-align: center; }
        .thankyou { font-size: 10px; font-weight: bold; text-align: center; margin-top: 12px; color: #b4945c; }
        @media print { body { background: white; padding: 0; margin: 0; } .receipt { box-shadow: none; max-width: 100%; } .print-button { display: none; } }
        .print-button { display: block; width: calc(100% - 40px); margin: 0 20px 20px 20px; padding: 10px; background: #1a1a1a; color: white; border: none; border-radius: 6px; font-size: 12px; font-weight: bold; cursor: pointer; font-family: monospace; }
        .print-button:hover { background: #b4945c; }
        .status-badge { display: inline-block; background: #f97316; color: white; font-size: 8px; padding: 2px 6px; border-radius: 12px; margin-top: 8px; }
        .status-paid { background: #22c55e; }
      </style>
    </head>
    <body>
      <div class="receipt">
        <div class="receipt-content">
          <div class="header">
            <div class="store-name">✨ SPARKLING GEMS ✨</div>
            <div class="store-tagline">LUXURY • AUTHENTICITY • ELEGANCE</div>
            <div class="store-address">2F, San Antonio Building, Corumi<br>Quezon City, 1012 Metro Manila<br>Tel: (02) 1234 5678 | VAT REG TIN: 123-456-789-000</div>
            <div class="receipt-title">OFFICIAL RECEIPT</div>
          </div>
          
          <div class="info-section">
            <div class="info-row"><span class="info-label">RECEIPT NO:</span><span class="info-value">${order.order_number || order.id}</span></div>
            <div class="info-row"><span class="info-label">DATE & TIME:</span><span class="info-value">${formatReceiptDate(orderDate)}</span></div>
            <div class="info-row"><span class="info-label">TRANSACTION ID:</span><span class="info-value">${order.transactionId || 'PENDING'}</span></div>
            <div class="info-row"><span class="info-label">SALES ASSISTANT:</span><span class="info-value">Online System</span></div>
            <div class="info-row"><span class="info-label">POS TERMINAL:</span><span class="info-value">WEB-001</span></div>
            <div class="info-row"><span class="info-label">STATUS:</span><span class="info-value"><span class="status-badge ${isPaid ? 'status-paid' : ''}" style="display: inline-block; background: ${isPaid ? '#22c55e' : '#f97316'}; color: white; padding: 2px 6px; border-radius: 12px;">${isPaid ? 'PAID' : 'PENDING VERIFICATION'}</span></span></div>
          </div>
          
          <div class="info-section">
            <div class="info-row"><span class="info-label">CUSTOMER:</span><span class="info-value">${order.customerName || order.customer_name || 'Guest Customer'}</span></div>
            <div class="info-row"><span class="info-label">EMAIL:</span><span class="info-value">${order.customer_email || profileData.email || 'N/A'}</span></div>
            <div class="info-row"><span class="info-label">CONTACT:</span><span class="info-value">${order.customer_phone || profileData.phone || 'N/A'}</span></div>
            ${isPickup ? `
            <div class="info-row"><span class="info-label">PICKUP PERSON:</span><span class="info-value">${(order as any).pickup_name || order.customerName}</span></div>
            <div class="info-row"><span class="info-label">PICKUP CONTACT:</span><span class="info-value">${(order as any).pickup_phone || order.customer_phone}</span></div>
            ` : `
            <div class="info-row"><span class="info-label">DELIVERY ADDRESS:</span><span class="info-value">${order.shippingAddress || order.shipping_address || 'Store Pickup'}</span></div>
            `}
          </div>
          
          <div class="items-header"><span>ITEM</span><span>QTY</span><span>AMOUNT</span></div>
          ${order.items && order.items.map(item => `
            <div class="item-row">
              <span class="item-name">${(item.name || item.product_name || 'Product').substring(0, 35)}${(item.name || '').length > 35 ? '...' : ''}</span>
              <span class="item-qty">${item.quantity || 1}</span>
              <span class="item-price">₱ ${((item.price || item.product_price || 0) * (item.quantity || 1)).toLocaleString()}</span>
            </div>
            ${item.description ? `<div class="item-row" style="margin-top: -4px;"><span class="item-name" style="font-size: 7px; color: #888;">  ${item.description.substring(0, 40)}${item.description.length > 40 ? '...' : ''}</span><span></span><span></span></div>` : ''}
          `).join('')}
          
          <div class="totals-section">
            <div class="total-row"><span>Subtotal:</span><span>₱ ${(order.subtotal || order.total || 0).toLocaleString()}</span></div>
            <div class="total-row"><span>Shipping Fee:</span><span>${(order.shipping_fee || 0) > 0 ? `₱ ${(order.shipping_fee || 0).toLocaleString()}` : 'FREE'}</span></div>
            <div class="total-row"><span>Discount:</span><span>₱ 0.00</span></div>
            <div class="total-row"><span>VAT (12%):</span><span>₱ ${((order.total || order.totalAmount || 0) * 0.12).toLocaleString()}</span></div>
            <div class="total-row grand-total"><span>TOTAL AMOUNT DUE:</span><span>₱ ${(order.total || order.totalAmount || 0).toLocaleString()}</span></div>
          </div>
          
          <div class="payment-method">
            <div class="info-row"><span class="info-label">PAYMENT METHOD:</span><span class="info-value">${order.paymentMethod || order.payment_method || 'Bank Transfer'}</span></div>
            ${order.transactionId ? `<div class="info-row"><span class="info-label">REFERENCE NO:</span><span class="info-value">${order.transactionId}</span></div>` : ''}
          </div>
          
          ${isPickup ? `
          <div class="info-section" style="margin-top: 12px; background: #f9f9f9; padding: 8px;">
            <div class="info-row"><span class="info-label">PICKUP LOCATION:</span><span class="info-value">Sparkling Gems Jewelry Shop</span></div>
            <div class="info-row"><span class="info-label">PICKUP HOURS:</span><span class="info-value">Mon-Sat, 10:00 AM - 7:00 PM</span></div>
            <div class="info-row"><span class="info-label">REQUIREMENTS:</span><span class="info-value">Valid ID & Order Confirmation</span></div>
          </div>
          ` : `
          <div class="info-section" style="margin-top: 12px; background: #f9f9f9; padding: 8px;">
            <div class="info-row"><span class="info-label">TRACKING NUMBER:</span><span class="info-value">${order.trackingNumber || order.tracking_number || 'To be assigned'}</span></div>
            <div class="info-row"><span class="info-label">ESTIMATED DELIVERY:</span><span class="info-value">3-5 business days after shipment</span></div>
          </div>
          `}
          
          <div class="footer">
            <div>This is a system-generated official receipt.</div>
            <div>For inquiries, contact concierge@sparklinggems.ph</div>
            <div>Terms and conditions apply.</div>
            <div class="qr-note">⚡ Verified by Sparkling Gems Security System ⚡</div>
            <div class="thankyou">✨ THANK YOU FOR SHOPPING WITH US! ✨</div>
            <div style="margin-top: 8px; font-size: 7px;">Visit us at sparklinggems.shop</div>
          </div>
        </div>
        <button class="print-button" onclick="window.print();">🖨️ PRINT RECEIPT</button>
      </div>
      <script>window.onload = function() { setTimeout(() => { window.print(); }, 500); };</script>
    </body>
    </html>
  `;
};

// Print receipt function
const printReceipt = (order: MemberOrder, user: User | null, profileData: any) => {
  const receiptHTML = generateReceiptHTML(order, user, profileData);
  const printWindow = window.open('', '_blank', 'width=500,height=700');
  if (printWindow) {
    printWindow.document.write(receiptHTML);
    printWindow.document.close();
  }
};

const Checkout: React.FC<CheckoutProps> = ({ 
    cart, 
    onPlaceOrder, 
    onNavigate,
    onSelectProduct,
    onUpdateCartQty,
    onRemoveFromCart,
    onClearCart,
    directCheckoutItem: propDirectCheckoutItem,
    user,
    onRefreshWishlist
}) => {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<MemberOrder | null>(null);
  const [paymentProof, setPaymentProof] = useState<string | null>(null);
  const [paymentProofFile, setPaymentProofFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userId, setUserId] = useState<number | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  
  // Guest checkout modal state
  const [showGuestModal, setShowGuestModal] = useState(false);
  
  // Profile data for receipt
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: ''
  });
  
  // Delivery type state
  const [deliveryType, setDeliveryType] = useState<'shipping' | 'pickup'>('shipping');
  
  // Bank details state
  const [bankDetails, setBankDetails] = useState<BankDetail[]>([]);
  const [selectedBank, setSelectedBank] = useState<BankDetail | null>(null);
  const [loadingBanks, setLoadingBanks] = useState(true);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    paymentMethod: 'bank'
  });

  // Pickup form data
  const [pickupData, setPickupData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  // Local direct checkout item (for recovery)
  const [localDirectCheckoutItem, setLocalDirectCheckoutItem] = useState<{ product: Product; quantity: number } | null>(propDirectCheckoutItem || null);

  // Check if user is logged in
  const isLoggedIn = !!user && !!user.id;

  const isWishlistCheckout = (): boolean => {
    try {
      const wishlistCheckout = sessionStorage.getItem('wishlistCheckout');
      if (!wishlistCheckout) return false;
      const parsed = JSON.parse(wishlistCheckout);
      return parsed?.source === 'wishlist' && Array.isArray(parsed?.items) && parsed.items.length > 0;
    } catch (error) {
      console.error('Error checking wishlist checkout session:', error);
      return false;
    }
  };

  // Get wishlist product IDs for removal
  const getWishlistProductIds = (): number[] => {
    try {
      const wishlistCheckout = sessionStorage.getItem('wishlistCheckout');
      if (!wishlistCheckout) return [];
      const parsed = JSON.parse(wishlistCheckout);
      if (parsed?.source === 'wishlist' && Array.isArray(parsed?.items)) {
        return parsed.items.map((item: any) => item.product.id);
      }
    } catch (error) {
      console.error('Error getting wishlist product IDs:', error);
    }
    return [];
  };

  // Remove items from wishlist after successful checkout
  const removeFromWishlist = async (productIds: number[]) => {
    if (!productIds.length) return;
    
    const currentUserId = user?.id || userId;
    const guestId = localStorage.getItem('guest_id');
    
    if (!currentUserId && !guestId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/remove-bulk.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUserId ? parseInt(String(currentUserId)) : null,
          guest_id: guestId || null,
          product_ids: productIds
        })
      });
      
      const data = await response.json();
      if (data.success) {
        console.log(`Removed ${data.deleted_count} items from wishlist`);
        if (onRefreshWishlist) {
          onRefreshWishlist();
        }
        window.dispatchEvent(new CustomEvent('wishlist-updated'));
      }
    } catch (error) {
      console.error('Error removing wishlist items:', error);
    }
  };

  // Show guest modal if not logged in and trying to checkout
  useEffect(() => {
    if (!isLoggedIn && (cart.length > 0 || localDirectCheckoutItem)) {
      setShowGuestModal(true);
    }
  }, [isLoggedIn, cart.length, localDirectCheckoutItem]);

  // Handle continue as guest - just go back to shop
  const handleContinueAsGuest = () => {
    setShowGuestModal(false);
    onNavigate('shop');
  };

  // Handle login click
  const handleLoginClick = () => {
    setShowGuestModal(false);
    onNavigate('auth');
  };

  // Handle register click
  const handleRegisterClick = () => {
    setShowGuestModal(false);
    onNavigate('register');
  };

  // Load profile data for receipt
  useEffect(() => {
    if (user?.id) {
      loadProfileForReceipt();
    }
  }, [user]);

  const loadProfileForReceipt = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/get-profile.php?user_id=${user?.id}`);
      const data = await response.json();
      if (data.success && data.user) {
        setProfileData({
          firstName: data.user.firstName || '',
          lastName: data.user.lastName || '',
          email: data.user.email || '',
          phone: data.user.phone || '',
          address: data.user.address || '',
          city: data.user.city || '',
          postalCode: data.user.postalCode || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  // Check for stored checkout data on component mount (for page reload recovery)
  useEffect(() => {
    const storedCheckout = sessionStorage.getItem('directCheckout');
    if (storedCheckout && !propDirectCheckoutItem && cart.length === 0) {
      try {
        const parsed = JSON.parse(storedCheckout);
        console.log('Recovered checkout data from sessionStorage:', parsed);
        setLocalDirectCheckoutItem({
          product: parsed.product,
          quantity: parsed.quantity
        });
        sessionStorage.removeItem('directCheckout');
      } catch (e) {
        console.error('Error parsing stored checkout:', e);
      }
    }
  }, [propDirectCheckoutItem, cart.length]);

  // Load user profile and set userId from user prop - WITH AUTOFILL
  useEffect(() => {
    const loadUserProfile = async () => {
      setIsLoadingProfile(true);
      try {
        let currentUserId = user?.id || null;
        
        if (!currentUserId) {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const parsedUser = JSON.parse(userStr);
            currentUserId = parsedUser.id;
          }
        }
        
        if (!currentUserId) {
          const storedUserId = localStorage.getItem('userId');
          if (storedUserId) {
            currentUserId = parseInt(storedUserId);
          }
        }
        
        if (!currentUserId) {
          console.log('No user logged in');
          setUserId(null);
          setIsLoadingProfile(false);
          return;
        }
        
        setUserId(currentUserId);
        console.log('Checkout using user_id:', currentUserId);
        
        const response = await fetch(`${API_BASE_URL}/users/get-profile.php?user_id=${currentUserId}`);
        const data = await response.json();
        
        if (data.success && data.user) {
          const profile = data.user;
          
          setFormData(prev => ({
            ...prev,
            firstName: profile.firstName || profile.first_name || '',
            lastName: profile.lastName || profile.last_name || '',
            email: profile.email || user?.email || '',
            phone: profile.phone || user?.phone || '',
            address: profile.address || '',
            city: profile.city || '',
            postalCode: profile.postalCode || profile.postal_code || ''
          }));
          
          const fullName = `${profile.firstName || profile.first_name || ''} ${profile.lastName || profile.last_name || ''}`.trim();
          setPickupData(prev => ({
            ...prev,
            name: fullName || user?.name || '',
            phone: profile.phone || user?.phone || '',
            email: profile.email || user?.email || ''
          }));
          
          setProfileData({
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            email: profile.email || '',
            phone: profile.phone || '',
            address: profile.address || '',
            city: profile.city || '',
            postalCode: profile.postalCode || ''
          });
          
          console.log('✅ Form auto-filled with profile data');
        } else if (user) {
          setFormData(prev => ({
            ...prev,
            firstName: user.firstName || user.name?.split(' ')[0] || '',
            lastName: user.lastName || (user.name?.split(' ').slice(1).join(' ') || ''),
            email: user.email || '',
            phone: user.phone || ''
          }));
          
          setPickupData(prev => ({
            ...prev,
            name: user.name || '',
            phone: user.phone || '',
            email: user.email || ''
          }));
          
          setProfileData({
            firstName: user.firstName || user.name?.split(' ')[0] || '',
            lastName: user.lastName || (user.name?.split(' ').slice(1).join(' ') || ''),
            email: user.email || '',
            phone: user.phone || '',
            address: '',
            city: '',
            postalCode: ''
          });
          
          console.log('✅ Form auto-filled with user object data');
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    
    loadUserProfile();
  }, [user]);

  // Determine which items to display
  const displayItems = localDirectCheckoutItem 
    ? [{ ...localDirectCheckoutItem.product, quantity: localDirectCheckoutItem.quantity }] 
    : cart;

  const subtotal = displayItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const shipping = 0;
  const total = subtotal + shipping;

  // Fetch bank details on component mount
  useEffect(() => {
    const loadBankDetails = async () => {
      setLoadingBanks(true);
      const banks = await fetchBankDetails();
      setBankDetails(banks);
      const defaultBank = getDefaultBank(banks);
      if (defaultBank) setSelectedBank(defaultBank);
      setLoadingBanks(false);
    };
    loadBankDetails();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handlePickupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPickupData({ ...pickupData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (deliveryType === 'shipping') {
      setFormData({ ...formData, phone: value });
    } else {
      setPickupData({ ...pickupData, phone: value });
    }
    if (error) setError(null);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            return;
        }
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (JPG, PNG)');
            return;
        }
        setPaymentProofFile(file);
        const url = URL.createObjectURL(file);
        setPaymentProof(url);
        setError(null);
    }
  };

  const updateProductStock = async (productId: number, quantity: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/update-stock.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, quantity: quantity, operation: 'decrease' })
      });
      const data = await response.json();
      if (!data.success) console.error('Failed to update stock for product:', productId);
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  const clearCartFromDatabase = async () => {
    const storedUserId = localStorage.getItem('userId');
    const guestId = localStorage.getItem('guest_id');
    if (!storedUserId && !guestId) return;
    try {
      await fetch(`${API_BASE_URL}/cart/clear.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: storedUserId ? parseInt(storedUserId) : null, guest_id: guestId || null })
      });
    } catch (error) {
      console.error('Error clearing cart from database:', error);
    }
  };

  const validateForm = () => {
    if (!formData.email.trim()) return 'Email is required';
    if (!formData.email.includes('@')) return 'Valid email is required';
    
    const phone = deliveryType === 'shipping' ? formData.phone : pickupData.phone;
    if (!phone.trim()) return 'Phone number is required';
    if (phone.length < 10) return 'Phone number must be at least 10 digits';
    
    if (deliveryType === 'shipping') {
      if (!formData.firstName.trim()) return 'First name is required';
      if (!formData.lastName.trim()) return 'Last name is required';
      if (!formData.address.trim()) return 'Address is required';
      if (!formData.city.trim()) return 'City is required';
      if (!formData.postalCode.trim()) return 'Postal code is required';
    } else {
      if (!pickupData.name.trim()) return 'Name is required for pickup';
    }
    
    if (!paymentProofFile) return "Please upload a proof of payment (screenshot or receipt) to proceed.";
    if (!selectedBank) return "Bank details are loading. Please wait or refresh the page.";
    return null;
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // handleSubmit function
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isLoggedIn) {
      setShowGuestModal(true);
      return;
    }
    
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }

    setIsProcessing(true);
    setError(null);
    setUploadProgress(30);
    
    try {
        let paymentProofBase64 = await fileToBase64(paymentProofFile!);
        const originalSize = paymentProofBase64.length;
        
        if (originalSize > 1 * 1024 * 1024) {
            let compressed = await compressImage(paymentProofBase64, 800, 0.6);
            let newSize = compressed.length;
            
            if (newSize > 1.5 * 1024 * 1024) {
                compressed = await compressImage(paymentProofBase64, 700, 0.5);
                newSize = compressed.length;
            }
            
            if (newSize > 2 * 1024 * 1024) {
                compressed = await compressImage(paymentProofBase64, 600, 0.4);
                newSize = compressed.length;
            }
            
            paymentProofBase64 = compressed;
        }
        
        if (paymentProofBase64.length > 3 * 1024 * 1024) {
            throw new Error('Image is too large. Please upload a smaller image (max 3MB after compression).');
        }
        
        setUploadProgress(50);
        const transactionId = `TXN-${Date.now().toString().slice(-8)}-${Math.floor(Math.random() * 1000)}`;
        
        let finalUserId = userId;
        if (!finalUserId && user?.id) finalUserId = user.id;
        
        console.log('Creating order with user_id:', finalUserId);
        
        const orderData: any = {
            user_id: finalUserId,
            customer: {
                firstName: deliveryType === 'shipping' ? formData.firstName : pickupData.name.split(' ')[0] || '',
                lastName: deliveryType === 'shipping' ? formData.lastName : pickupData.name.split(' ').slice(1).join(' ') || '',
                email: formData.email,
                address: deliveryType === 'shipping' ? formData.address : 'Store Pickup',
                city: deliveryType === 'shipping' ? formData.city : 'Makati',
                postalCode: deliveryType === 'shipping' ? formData.postalCode : '1200',
                phone: deliveryType === 'shipping' ? formData.phone : pickupData.phone
            },
            items: displayItems.map(item => ({
                product_id: item.id,
                id: item.id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                image: repairBase64Image(item.image),
                category: item.category || null,
                description: item.description || null,
                // Add jewelry-specific fields
                weight: item.weight,
                karat: item.karat,
                goldType: item.goldType,
                condition: item.condition
            })),
            paymentMethod: 'Bank Transfer',
            deliveryType: deliveryType,
            subtotal,
            shipping,
            total,
            transactionId,
            orderDate: new Date().toISOString()
        };

        if (deliveryType === 'pickup') {
            orderData.pickup_name = pickupData.name;
            orderData.pickup_phone = pickupData.phone;
            orderData.pickup_email = formData.email;
        }

        setUploadProgress(70);
        
        const orderResponse = await fetch(`${API_BASE_URL}/orders/create.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderData)
        });

        const orderResponseText = await orderResponse.text();
        let orderResult;
        try { orderResult = JSON.parse(orderResponseText); } catch (e) { throw new Error('Invalid response from server'); }
        
        if (!orderResult.success) throw new Error(orderResult.error || 'Failed to create order');
        
        const orderId = orderResult.order_id;
        setUploadProgress(85);

        const proofData = { 
            order_id: orderId, 
            payment_proof: paymentProofBase64, 
            transaction_id: transactionId, 
            user_id: finalUserId 
        };
        
        const proofResponse = await fetch(`${API_BASE_URL}/orders/upload-proof.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(proofData)
        });
        
        const proofResult = await proofResponse.json();
        
        if (!proofResult.success) {
            console.error('❌ Proof upload failed:', proofResult.error);
            alert(`Warning: ${proofResult.error}. Your order was created but proof may not be saved. Please contact support with order #${orderResult.order_number || orderId}.`);
        }

        for (const item of displayItems) {
            await updateProductStock(item.id, item.quantity);
        }

        const wishlistCheckoutActive = isWishlistCheckout();
        
        if (wishlistCheckoutActive) {
            const productIds = getWishlistProductIds();
            if (productIds.length > 0) {
                await removeFromWishlist(productIds);
                sessionStorage.removeItem('wishlistCheckout');
            }
        }

        if (!wishlistCheckoutActive) {
          await clearCartFromDatabase();
        }

        setUploadProgress(100);

        const newOrder: MemberOrder = {
            id: orderResult.order_number || `SG-${orderId}`,
            order_number: orderResult.order_number || `SG-${orderId}`,
            userId: finalUserId || 1,
            customerName: deliveryType === 'shipping' ? `${formData.firstName} ${formData.lastName}` : pickupData.name,
            customer_name: deliveryType === 'shipping' ? `${formData.firstName} ${formData.lastName}` : pickupData.name,
            customer_email: formData.email,
            customer_phone: deliveryType === 'shipping' ? formData.phone : pickupData.phone,
            date: new Date().toLocaleDateString(),
            total: total,
            totalAmount: total,
            subtotal: subtotal,
            shipping_fee: shipping,
            status: 'Pending',
            paymentMethod: 'Bank Transfer',
            payment_method: 'Bank Transfer',
            deliveryType: deliveryType,
            transactionId: transactionId,
            transaction_id: transactionId,
            shippingAddress: deliveryType === 'shipping' ? `${formData.address}, ${formData.city} ${formData.postalCode}` : 'Store Pickup - Sparkling Gems Jewelry Shop',
            shipping_address: deliveryType === 'shipping' ? `${formData.address}, ${formData.city} ${formData.postalCode}` : 'Store Pickup - Sparkling Gems Jewelry Shop',
            city: deliveryType === 'shipping' ? formData.city : 'Makati',
            postal_code: deliveryType === 'shipping' ? formData.postalCode : '1200',
            items: displayItems.map(c => ({
                id: c.id,
                product_id: c.id,
                name: c.name,
                description: c.description || '',
                price: c.price,
                image: repairBase64Image(c.image),
                category: c.category,
                quantity: c.quantity,
                // Preserve jewelry-specific fields
                weight: c.weight,
                karat: c.karat,
                goldType: c.goldType,
                condition: c.condition
            })),
            paymentProof: paymentProofBase64,
            payment_proof: paymentProofBase64,
            trackingNumber: deliveryType === 'shipping' ? `TRK-${Date.now().toString().slice(-8)}` : undefined,
            tracking_number: deliveryType === 'shipping' ? `TRK-${Date.now().toString().slice(-8)}` : undefined,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            file_path: undefined
        };
        
        if (deliveryType === 'pickup') {
            (newOrder as any).pickup_name = pickupData.name;
            (newOrder as any).pickup_phone = pickupData.phone;
            (newOrder as any).pickup_email = formData.email;
        }
        
        onPlaceOrder(newOrder);

        if (!wishlistCheckoutActive && onClearCart) {
          onClearCart();
        }

        if (!wishlistCheckoutActive) {
          localStorage.removeItem('cartCount');
          localStorage.removeItem('cart');
          window.dispatchEvent(new Event('storage'));
        }

        setCompletedOrder(newOrder);
        setStep('success');
        
    } catch (error) {
        console.error('❌ Checkout error:', error);
        setError(error instanceof Error ? error.message : 'Connection error. Please try again.');
    } finally {
        setIsProcessing(false);
        setUploadProgress(0);
    }
  };

  // Success page component
  if (step === 'success' && completedOrder) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-24 pb-12 px-6">
        <div className="max-w-2xl w-full bg-white border border-stone-200 shadow-2xl rounded-sm animate-fade-in relative overflow-hidden">
          <div className={`h-2 w-full bg-gradient-to-r ${completedOrder.status === 'Pending' ? 'from-orange-300 via-orange-500 to-orange-300' : 'from-gold-300 via-gold-500 to-gold-300'}`}></div>
          <div className="p-8 md:p-12">
              <div className="text-center border-b border-stone-100 pb-8 mb-8">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 ${completedOrder.status === 'Pending' ? 'bg-orange-50 border-orange-200 text-orange-600' : 'bg-green-50 border-green-200 text-green-600'}`}>
                    {completedOrder.status === 'Pending' ? <Clock size={40} /> : <CheckCircle2 size={40} />}
                  </div>
                  <h2 className="font-serif text-3xl text-stone-900 mb-2">
                      {completedOrder.status === 'Pending' ? 'Payment Pending Verification' : 'Payment Confirmed'}
                  </h2>
                  <p className="text-stone-500 text-sm max-w-md mx-auto">
                      {completedOrder.status === 'Pending' 
                        ? "We have received your proof of payment. Our concierge team will verify your transaction within 24 hours." 
                        : "Thank you for your purchase. A receipt has been sent to your email."}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-stone-100 rounded-full">
                      <span className="text-xs font-medium">{(completedOrder as any).deliveryType === 'pickup' ? '📍 Pickup Order' : '🚚 Shipping Order'}</span>
                  </div>
              </div>
              <div className="flex gap-4">
                  <button 
                    onClick={() => printReceipt(completedOrder, user ?? null, profileData)} 
                    className="flex-1 flex items-center justify-center gap-2 border border-stone-200 py-3 text-stone-600 text-xs uppercase tracking-widest hover:border-stone-900 hover:text-stone-900 transition-colors rounded-sm"
                  >
                      <Printer size={16} /> Print Receipt
                  </button>
                  <button onClick={() => onNavigate('shop')} className="flex-1 bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors rounded-sm">
                    Continue Shopping
                  </button>
              </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!localDirectCheckoutItem && cart.length === 0 && step === 'form') {
    return (
        <div className="min-h-screen bg-stone-50 pt-32 pb-20 px-6 text-center">
            <h2 className="font-serif text-3xl text-stone-900 mb-4">Your Bag is Empty</h2>
            <p className="text-stone-500 mb-8">It looks like you haven't added any treasures yet.</p>
            <button onClick={() => onNavigate('shop')} className="bg-stone-900 text-white px-8 py-3 uppercase text-xs tracking-[0.2em] hover:bg-gold-600 transition-colors rounded-sm">
                Continue Shopping
            </button>
        </div>
    );
  }

  // Loading state
  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-stone-50 pt-32 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-gold-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-500 text-sm">Loading your information...</p>
        </div>
      </div>
    );
  }

  // If not logged in, show only the modal
  if (!isLoggedIn && (cart.length > 0 || localDirectCheckoutItem)) {
    return (
      <div className="min-h-screen bg-stone-50 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="fixed inset-0 z-[400] flex items-center justify-center">
            <div className="absolute inset-0 bg-stone-900/80 backdrop-blur-md"></div>
            <div className="relative bg-white w-full max-w-md rounded-sm shadow-2xl mx-4 overflow-hidden animate-fade-up-enter">
              <div className="bg-gradient-to-r from-gold-600 to-gold-500 text-white p-6 text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles size={32} className="text-white" />
                </div>
                <h3 className="font-serif text-2xl mb-2">Welcome to Sparkling Gems</h3>
                <p className="text-sm text-white/90">Sign in to complete your purchase and unlock exclusive benefits</p>
              </div>
              
              <div className="p-8 space-y-6">
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm">
                  <p className="text-sm text-amber-800 font-medium mb-2">✨ Member Benefits ✨</p>
                  <ul className="space-y-2 text-xs text-amber-700">
                    <li className="flex items-center gap-2">✓ Track your order status in real-time</li>
                    <li className="flex items-center gap-2">✓ Earn loyalty points (1 point per ₱100 spent)</li>
                    <li className="flex items-center gap-2">✓ Access exclusive member-only collections</li>
                    <li className="flex items-center gap-2">✓ Faster checkout with saved addresses</li>
                    <li className="flex items-center gap-2">✓ Receive order updates via email/SMS</li>
                    <li className="flex items-center gap-2">✓ Priority customer support</li>
                  </ul>
                </div>
                
                <div className="space-y-3">
                  <button 
                    onClick={handleLoginClick}
                    className="w-full bg-stone-900 text-white py-3 rounded-sm hover:bg-gold-600 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
                  >
                    <LogIn size={18} />
                    Login to Your Account
                  </button>
                  
                  <button 
                    onClick={handleRegisterClick}
                    className="w-full border border-stone-300 text-stone-700 py-3 rounded-sm hover:bg-stone-50 transition-colors flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
                  >
                    <UserPlus size={18} />
                    Create New Account
                  </button>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-stone-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-4 text-stone-400">or</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleContinueAsGuest}
                  className="w-full text-stone-500 py-2 text-sm hover:text-stone-700 transition-colors flex items-center justify-center gap-2"
                >
                  Continue Browsing
                </button>
                
                <p className="text-center text-[10px] text-stone-400 mt-4">
                  By continuing, you agree to our <button onClick={() => onNavigate('privacy-policy')} className="text-gold-600 hover:underline">Privacy Policy</button> and <button className="text-gold-600 hover:underline">Terms of Service</button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main checkout form
  return (
    <div className="min-h-screen bg-stone-50 pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <button onClick={() => onNavigate('shop')} className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-8 transition-colors">
          <ArrowLeft size={14} /> Continue Shopping
        </button>
        <h1 className="font-serif text-4xl text-stone-900 mb-12">Secure Checkout</h1>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded-sm flex items-start gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {isProcessing && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-700">Processing your order...</span>
              <span className="text-xs text-blue-600">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24">
          
          <div className="flex-1">
            <form id="checkout-form" onSubmit={handleSubmit} className="space-y-12">
              
              <section>
                <div className="flex items-center gap-2 mb-6 border-b border-stone-200 pb-2">
                    <Truck size={18} className="text-gold-600" />
                    <h3 className="font-serif text-xl text-stone-900">Delivery Method</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <button type="button" onClick={() => setDeliveryType('shipping')} className={`p-6 border-2 rounded-sm transition-all flex flex-col items-center gap-3 ${deliveryType === 'shipping' ? 'border-gold-600 bg-gold-50' : 'border-stone-200 hover:border-gold-300'}`}>
                        <Truck size={32} className={deliveryType === 'shipping' ? 'text-gold-600' : 'text-stone-400'} />
                        <span className={`font-bold ${deliveryType === 'shipping' ? 'text-gold-600' : 'text-stone-600'}`}>Ship to Address</span>
                        <span className="text-xs text-stone-500">Home or office delivery</span>
                    </button>
                    <button type="button" onClick={() => setDeliveryType('pickup')} className={`p-6 border-2 rounded-sm transition-all flex flex-col items-center gap-3 ${deliveryType === 'pickup' ? 'border-gold-600 bg-gold-50' : 'border-stone-200 hover:border-gold-300'}`}>
                        <Store size={32} className={deliveryType === 'pickup' ? 'text-gold-600' : 'text-stone-400'} />
                        <span className={`font-bold ${deliveryType === 'pickup' ? 'text-gold-600' : 'text-stone-600'}`}>Store Pickup</span>
                        <span className="text-xs text-stone-500">Free • Ready in 2-3 days</span>
                    </button>
                </div>
                
                {deliveryType === 'pickup' && (
                    <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm mb-4">
                        <div className="flex items-start gap-3">
                            <Store size={20} className="text-amber-600 mt-0.5" />
                            <div>
                                <h4 className="font-bold text-amber-800 text-sm">Sparkling Gems Jewelry Shop</h4>
                                <p className="text-xs text-amber-700 mt-1">2F, San Antonio Building, Corumi, Quezon City, 1012 Metro Manila</p>
                                <p className="text-xs text-amber-600 mt-2">⏰ Pickup Hours: Mon-Sat, 10am - 7pm</p>
                            </div>
                        </div>
                    </div>
                )}
              </section>
              
              {deliveryType === 'shipping' && (
                <section>
                  <div className="flex items-center gap-2 mb-6 border-b border-stone-200 pb-2">
                      <Truck size={18} className="text-gold-600" />
                      <h3 className="font-serif text-xl text-stone-900">Shipping Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">First Name *</label>
                      <input required name="firstName" value={formData.firstName} onChange={handleChange} type="text" className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" placeholder="e.g., Juan" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Last Name *</label>
                      <input required name="lastName" value={formData.lastName} onChange={handleChange} type="text" className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" placeholder="e.g., Dela Cruz" />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Email Address *</label>
                      <input required name="email" value={formData.email} onChange={handleChange} type="email" className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" placeholder="your@email.com" />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Street Address *</label>
                      <input required name="address" value={formData.address} onChange={handleChange} type="text" className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" placeholder="123 Street Name" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">City *</label>
                      <input required name="city" value={formData.city} onChange={handleChange} type="text" className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" placeholder="Makati" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Postal Code *</label>
                      <input required name="postalCode" value={formData.postalCode} onChange={handleChange} type="text" className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" placeholder="1200" />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Phone *</label>
                      <input required name="phone" value={formData.phone} onChange={handlePhoneChange} type="tel" inputMode="numeric" pattern="[0-9]*" placeholder="e.g. 09171234567" maxLength={11} className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" />
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-stone-400 italic">
                    Your information has been auto-filled from your profile. You can edit if needed.
                  </div>
                </section>
              )}
              
              {deliveryType === 'pickup' && (
                <section>
                  <div className="flex items-center gap-2 mb-6 border-b border-stone-200 pb-2">
                      <Store size={18} className="text-gold-600" />
                      <h3 className="font-serif text-xl text-stone-900">Pickup Information</h3>
                  </div>
                  <div className="grid grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Full Name *</label>
                      <input required name="name" value={pickupData.name} onChange={handlePickupChange} type="text" placeholder="Person picking up the order" className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Phone Number *</label>
                      <input required name="phone" value={pickupData.phone} onChange={handlePhoneChange} type="tel" inputMode="numeric" pattern="[0-9]*" placeholder="e.g. 09171234567" maxLength={11} className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Email Address *</label>
                      <input required name="email" value={formData.email} onChange={handleChange} type="email" placeholder="For order confirmation" className="w-full border-b border-stone-300 py-2 bg-transparent focus:border-gold-600 focus:outline-none transition-colors" />
                    </div>
                    <div className="bg-stone-50 p-4 rounded-sm border border-stone-200">
                      <p className="text-xs text-stone-600"><span className="font-bold">Note:</span> Please bring a valid ID when picking up your order. You will receive an email notification when your order is ready for pickup.</p>
                    </div>
                  </div>
                </section>
              )}

              <section>
                <div className="flex items-center gap-2 mb-6 border-b border-stone-200 pb-2">
                    <Building size={18} className="text-gold-600" />
                    <h3 className="font-serif text-xl text-stone-900">Bank Transfer Payment</h3>
                </div>

                <div className="space-y-6">
                    {loadingBanks ? (
                        <div className="text-center py-4"><div className="w-6 h-6 border-2 border-gold-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div><p className="text-xs text-stone-500">Loading bank details...</p></div>
                    ) : bankDetails.length === 0 ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center"><AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" /><p className="text-sm text-red-600">Bank details temporarily unavailable</p></div>
                    ) : (
                        <>
                            {bankDetails.length > 1 && (
                                <div className="mb-4"><label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">Select Bank</label>
                                <select value={selectedBank?.bankName} onChange={(e) => { const bank = bankDetails.find(b => b.bankName === e.target.value); setSelectedBank(bank || null); }} className="w-full p-3 border border-stone-300 rounded-sm focus:ring-2 focus:ring-gold-600 focus:border-transparent">
                                    {bankDetails.map(bank => (<option key={bank.bankName} value={bank.bankName}>{bank.bankName}</option>))}
                                </select></div>
                            )}
                            {selectedBank && (
                                <div className="bg-white p-4 border border-stone-200 rounded-sm mb-4">
                                    <p className="text-xs text-stone-500 uppercase tracking-widest mb-2">Bank Details</p>
                                    <div className="grid grid-cols-2 gap-4 mb-3"><div><p className="text-[9px] uppercase text-stone-400 mb-1">Bank</p><p className="text-sm font-bold text-stone-900">{selectedBank.bankName}</p>{selectedBank.branch && <p className="text-xs text-stone-500">{selectedBank.branch}</p>}</div><div><p className="text-[9px] uppercase text-stone-400 mb-1">Currency</p><p className="text-sm font-bold text-stone-900">{selectedBank.currency}</p></div></div>
                                    <div className="mb-3"><p className="text-[9px] uppercase text-stone-400 mb-1">Account Number</p><p className="font-mono text-base font-bold text-stone-900">{selectedBank.accountNumber}</p></div>
                                    <div className="mb-3"><p className="text-[9px] uppercase text-stone-400 mb-1">Account Name</p><p className="text-sm font-bold text-stone-900">{selectedBank.accountName}</p></div>
                                    <div className="mt-4 pt-3 border-t border-dashed border-stone-200"><p className="text-[9px] uppercase text-stone-400 mb-1">Amount to Pay</p><p className="text-xl font-bold text-gold-600">₱ {total.toLocaleString()}</p></div>
                                </div>
                            )}
                            {selectedBank?.instructions && (<div className="bg-blue-50 border border-blue-200 p-4 rounded-sm mb-4"><p className="text-xs text-blue-800 whitespace-pre-line">{selectedBank.instructions}</p></div>)}
                        </>
                    )}

                    <div className="border-t border-stone-200 pt-4">
                        <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-3">Upload Proof of Payment <span className="text-red-500">*</span></label>
                        {paymentProof ? (
                            <div className="mb-3"><div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-sm"><CheckCircle2 size={20} className="text-green-600" /><div className="flex-1"><p className="text-xs font-medium text-green-700">File selected: {paymentProofFile?.name}</p><p className="text-[10px] text-green-600">{(paymentProofFile?.size! / 1024).toFixed(1)} KB</p></div><button type="button" onClick={() => { setPaymentProof(null); setPaymentProofFile(null); }} className="text-xs text-red-500 hover:text-red-700">Remove</button></div></div>
                        ) : null}
                        <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-sm cursor-pointer transition-all ${paymentProof ? 'border-green-400 bg-green-50' : 'border-stone-300 hover:border-gold-500 hover:bg-white'}`}>
                            <div className="flex flex-col items-center justify-center pt-5 pb-6 text-stone-400"><Upload className="w-8 h-8 mb-2" /><p className="text-xs font-medium">Click to upload screenshot</p><p className="text-[10px] mt-1">JPG, PNG up to 5MB</p></div>
                            <input type="file" className="hidden" accept="image/jpeg,image/png,image/jpg" onChange={handleFileUpload} disabled={loadingBanks || bankDetails.length === 0} />
                        </label>
                        {!paymentProof && (<p className="text-[10px] text-red-400 mt-2 flex items-center gap-1"><AlertCircle size={10} /> Proof of payment is required</p>)}
                    </div>
                </div>
              </section>
            </form>
          </div>

          <div className="lg:w-[400px] flex-shrink-0">
            <div className="bg-white border border-stone-200 p-8 shadow-lg rounded-sm sticky top-32">
                <h3 className="font-serif text-xl text-stone-900 mb-6 pb-4 border-b border-stone-100">Order Summary</h3>
                <div className="space-y-6 mb-8 max-h-[400px] overflow-y-auto scrollbar-hide">
                    {displayItems.map((item) => {
                        // Get jewelry-specific details
                        const weight = item.weight ? (typeof item.weight === 'number' ? item.weight.toFixed(2) : item.weight) : null;
                        const karat = item.karat || null;
                        const goldType = item.goldType || null;
                        
                        return (
                            <div key={item.id} className="flex gap-4 py-4 border-b border-stone-100 last:border-0">
                                <div className="w-16 h-16 bg-stone-50 border border-stone-100 rounded-sm overflow-hidden flex-shrink-0 cursor-pointer" onClick={() => onSelectProduct?.(item)}>
                                    <img 
                                        src={repairBase64Image(item.image)} 
                                        alt={item.name} 
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                                        }}
                                    />
                                </div>
                                <div className="flex-1 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-start">
                                            <h4 className="font-serif text-sm text-stone-900 line-clamp-1 cursor-pointer hover:text-gold-600 transition-colors" onClick={() => onSelectProduct?.(item)}>
                                                {item.name}
                                            </h4>
                                            {!propDirectCheckoutItem && onRemoveFromCart && (
                                                <button onClick={() => onRemoveFromCart(item.id)} className="text-stone-300 hover:text-red-500 transition-colors">
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-stone-500 uppercase tracking-wide">{item.category}</p>
                                        
                                        {/* ADDED: Jewelry specifications display */}
                                        {(weight || karat || goldType) && (
                                            <div className="flex flex-wrap gap-2 mt-1">
                                                {karat && (
                                                    <span className="text-[9px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">
                                                        {karat}
                                                    </span>
                                                )}
                                                {goldType && (
                                                    <span className="text-[9px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">
                                                        {goldType}
                                                    </span>
                                                )}
                                                {weight && (
                                                    <span className="text-[9px] text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded">
                                                        {weight}g
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex justify-between items-end mt-2">
                                        <div className="flex items-center gap-3">
                                            {!propDirectCheckoutItem && onUpdateCartQty ? (
                                                <div className="flex items-center border border-stone-200 rounded-sm">
                                                    <button onClick={() => onUpdateCartQty(item.id, -1)} className="px-1.5 py-0.5 text-stone-500 hover:bg-stone-50">
                                                        <Minus size={10} />
                                                    </button>
                                                    <span className="px-1.5 text-xs font-mono min-w-[20px] text-center">{item.quantity}</span>
                                                    <button onClick={() => onUpdateCartQty(item.id, 1)} className="px-1.5 py-0.5 text-stone-500 hover:bg-stone-50">
                                                        <Plus size={10} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-stone-500">Qty: {item.quantity}</span>
                                            )}
                                            <button onClick={() => onSelectProduct?.(item)} className="text-[10px] font-bold text-gold-600 hover:underline">
                                                View
                                            </button>
                                        </div>
                                        <span className="font-medium text-sm text-stone-900">
                                            ₱ {(item.price * item.quantity).toLocaleString()}
                                        </span>
                                    </div>
                                    
                                    {/* Stock warning */}
                                    {item.stock !== undefined && item.stock <= 5 && item.stock > 0 && (
                                        <p className="text-[9px] text-amber-600 mt-1">Only {item.stock} left in stock</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="space-y-3 border-t border-stone-100 pt-6 mb-8">
                    <div className="flex justify-between text-sm">
                        <span className="text-stone-500">Subtotal</span>
                        <span className="text-stone-900 font-medium">₱ {subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-stone-500">{deliveryType === 'pickup' ? 'Pickup (Free)' : 'Shipping (Insured)'}</span>
                        <span className="text-emerald-600 font-bold uppercase text-xs">Free</span>
                    </div>
                    <div className="flex justify-between text-lg font-serif border-t border-stone-100 pt-4 mt-2">
                        <span className="text-stone-900">Total</span>
                        <span className="text-stone-900 font-bold">₱ {total.toLocaleString()}</span>
                    </div>
                </div>
                <button form="checkout-form" type="submit" disabled={isProcessing || loadingBanks || bankDetails.length === 0 || !paymentProof} className={`w-full py-4 uppercase text-xs tracking-[0.2em] transition-colors shadow-lg flex items-center justify-center gap-2 rounded-sm ${isProcessing || loadingBanks || bankDetails.length === 0 || !paymentProof ? 'bg-stone-300 text-stone-600 cursor-not-allowed' : 'bg-stone-900 text-white hover:bg-gold-600'}`}>
                    {isProcessing ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Processing...</>) : loadingBanks ? 'Loading Bank Details...' : bankDetails.length === 0 ? 'Bank Unavailable' : !paymentProof ? 'Upload Proof to Continue' : 'Place Order'}
                </button>
                <div className="mt-6 flex items-center justify-center gap-2 text-[10px] text-stone-400 uppercase tracking-widest">
                    <Lock size={10} /> Secure SSL Encrypted
                </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Checkout;